import { useQuery } from "@tanstack/react-query";
import type { IAuditLogEntry } from "@dealer-center/ddc-pages-account-franchise-audit-log-interface";
import { normalizeApiDate } from "@dealer-center/ddc-pages-account-franchise-audit-log-interface";

export type IAuditLogEntryView = IAuditLogEntry & {
  formattedModifiedDate: string;
};

const queryKeyBase = "ddc-pages-account-franchise-audit-log";

export function formatModifiedDate(date: string): string {
  if (!date) {
    return "N/A";
  }

  const parsedDate = new Date(normalizeApiDate(date));

  if (isNaN(parsedDate.getTime())) {
    return "Invalid Date";
  }

  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric"
  };

  return parsedDate.toLocaleString(undefined, options);
}

export function useAccountFranchiseAuditLog({
  accountId
}: {
  accountId: string;
}) {
  return useQuery({
    queryKey: [queryKeyBase, { accountId }],
    queryFn: () => getAccountFranchiseAuditLog(accountId),
    enabled: !!accountId && accountId.trim().length > 0,
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
}

const getAccountFranchiseAuditLog = async (
  accountId: string
): Promise<IAuditLogEntryView[]> => {
  const res = await fetch(
    `/landing/nxlambda/accountFranchiseAuditLog?accountId=${encodeURIComponent(accountId)}`
  );
  if (res.ok) {
    const data = (await res.json()) as IAuditLogEntry[];
    return data.map(entry => ({
      ...entry,
      formattedModifiedDate: formatModifiedDate(entry.modifiedDate)
    }));
  }
  throw new Error(
    `Failed to fetch account franchise audit log: ${res.status} ${res.statusText}`
  );
};
